import { SIMPLE_INPUT_FIELD_TYPES, supportsRecipientNameToggle } from './formFieldTypes'
import { normalizePrefillEditFieldIds, resolvePrefillEditFieldId } from './tableFieldUtils'

export interface PrefillEditableOnCondition {
  conditionActive?: boolean
  basedOnElementOfIndex?: number | null
  comparisionOperation?: string
  forValue?: string | { selectedx?: unknown[]; arrayVals?: string[]; str?: string }
}

export interface PrefillEditFieldDefinition {
  fieldId: number
  type: string
  label: string
  placeholder?: string
  accept?: string
  options?: Array<{ text: string; value: string; selected?: boolean }>
  /** When true, recipient must fill this field before submit. Defaults to true when unset. */
  isRequired?: boolean
  /** Shown when isRequired field is empty on submit. */
  requiredErrorMessage?: string
}

export const PREFILL_SETTINGS_FIELD_TYPES = [
  ...SIMPLE_INPUT_FIELD_TYPES,
  'select',
  'radio',
  'checkbox',
  'switch',
  'file',
  'table'
]

export const PREFILL_EDIT_FIELD_TYPES = [
  'text',
  'textarea',
  'email',
  'tel',
  'url',
  'password',
  'number',
  'date',
  'time',
  'datetime',
  'select',
  'radio',
  'checkbox',
  'switch',
  'file'
] as const

export type PrefillEditFieldType = (typeof PREFILL_EDIT_FIELD_TYPES)[number]

export const PREFILL_EDIT_FIELD_GROUPS: Array<{ label: string; types: PrefillEditFieldType[] }> = [
  {
    label: 'Text inputs',
    types: ['text', 'textarea', 'email', 'tel', 'url', 'password', 'number']
  },
  {
    label: 'Date & time',
    types: ['date', 'time', 'datetime']
  },
  {
    label: 'Choice',
    types: ['select', 'radio', 'checkbox', 'switch']
  },
  {
    label: 'Upload',
    types: ['file']
  }
]

export const PREFILL_EDIT_FIELD_TYPE_LABELS: Record<PrefillEditFieldType, string> = {
  text: 'Text',
  textarea: 'Textarea',
  email: 'Email',
  tel: 'Phone',
  url: 'URL',
  password: 'Password', // NOSONAR
  number: 'Number',
  date: 'Date',
  time: 'Time',
  datetime: 'Date & time',
  select: 'Dropdown',
  radio: 'Radio',
  checkbox: 'Checkbox',
  switch: 'Switch',
  file: 'File upload'
}

export const defaultOptionsForEditFieldType = (
  type: string
): Array<{ text: string; value: string }> | undefined => {
  if (type === 'select' || type === 'radio' || type === 'checkbox') {
    return [
      { text: 'Option 1', value: 'option_1' },
      { text: 'Option 2', value: 'option_2' }
    ]
  }
  return undefined
}

export const defaultPrefillEditable = (type?: string): boolean =>
  type !== 'email' && type !== 'phone'

export const defaultPrefillEditableOnCondition = (): PrefillEditableOnCondition => ({
  conditionActive: false,
  basedOnElementOfIndex: null,
  forValue: 'undefined',
  comparisionOperation: '='
})

export interface TableColumnPrefillSettings {
  prefillEditFields?: PrefillEditFieldDefinition[]
}

export interface TableRowPrefillSettings {
  allowEdit?: boolean
  editableColumnIndexes?: number[]
  prefillEditFields?: PrefillEditFieldDefinition[]
}

export const resizeTableRowPrefillSettings = (
  settings: TableRowPrefillSettings[] | undefined,
  count: number
): TableRowPrefillSettings[] => {
  const next = [...(settings || [])]
  while (next.length < count) {
    next.push({ allowEdit: false, editableColumnIndexes: [], prefillEditFields: [] })
  }
  return next.slice(0, count).map((entry) => ({
    allowEdit: entry?.allowEdit === true,
    editableColumnIndexes: Array.isArray(entry?.editableColumnIndexes)
      ? entry.editableColumnIndexes
      : [],
    prefillEditFields: Array.isArray(entry?.prefillEditFields) ? entry.prefillEditFields : []
  }))
}

export const resizeTableColumnPrefillSettings = (
  settings: TableColumnPrefillSettings[] | undefined,
  count: number
): TableColumnPrefillSettings[] => {
  const next = [...(settings || [])]
  while (next.length < count) {
    next.push({ prefillEditFields: [] })
  }
  return next.slice(0, count).map((entry) => ({
    prefillEditFields: Array.isArray(entry?.prefillEditFields) ? entry.prefillEditFields : []
  }))
}

export const getTableRowPrefillSettings = (
  element: any,
  rowIndex: number
): TableRowPrefillSettings =>
  element?.tableRowPrefillSettings?.[rowIndex] || {
    allowEdit: false,
    editableColumnIndexes: [],
    prefillEditFields: []
  }

export const getTableRowPrefillEditFields = (
  element: any,
  rowIndex: number
): PrefillEditFieldDefinition[] =>
  getTableRowPrefillSettings(element, rowIndex).prefillEditFields || []

export const isTableRowConditionalConfigured = (element: any, rowIndex: number): boolean => {
  const settings = getTableRowPrefillSettings(element, rowIndex)
  return (
    settings.allowEdit === true &&
    Array.isArray(settings.prefillEditFields) &&
    settings.prefillEditFields.length > 0
  )
}

export const getTableRowConditionalFieldCount = (element: any, rowIndex: number): number =>
  isTableRowConditionalConfigured(element, rowIndex)
    ? getTableRowPrefillEditFields(element, rowIndex).length
    : 0

export const tableHasRowConditionalEdit = (element: any): boolean => {
  const settings: TableRowPrefillSettings[] = element?.tableRowPrefillSettings || []
  return settings.some(
    (entry) =>
      entry?.allowEdit === true &&
      Array.isArray(entry?.prefillEditFields) &&
      entry.prefillEditFields.length > 0
  )
}

export const tableUsesPerRowEditConfig = (element: any): boolean => {
  const settings: TableRowPrefillSettings[] = element?.tableRowPrefillSettings || []
  return settings.some((entry) => entry?.allowEdit === true)
}

export const getTableColumnPrefillEditFields = (
  element: any,
  colIndex: number
): PrefillEditFieldDefinition[] =>
  element?.tableColumnPrefillSettings?.[colIndex]?.prefillEditFields || []

export const tableHasColumnConditionalEdit = (element: any): boolean => {
  if (element?.type !== 'table') return hasPrefillEditFields(element)
  const settings: TableColumnPrefillSettings[] = element?.tableColumnPrefillSettings || []
  return settings.some(
    (entry) => Array.isArray(entry?.prefillEditFields) && entry.prefillEditFields.length > 0
  )
}

export const tableHasTableConditionalEdit = (element: any): boolean => {
  if (element?.type !== 'table') return hasPrefillEditFields(element)
  return (
    tableHasRowConditionalEdit(element) ||
    tableHasColumnConditionalEdit(element) ||
    hasPrefillEditFields(element)
  )
}

export const hasPrefillEditFields = (element: any): boolean =>
  Array.isArray(element?.prefillEditFields) && element.prefillEditFields.length > 0

export const isConditionalPrefillEditMode = (element: any): boolean => {
  if (element?.prefillEditable === false) return false
  const hasFields =
    element?.type === 'table'
      ? tableHasTableConditionalEdit(element)
      : hasPrefillEditFields(element)
  if (!hasFields) return false
  if (element?.prefillEditMode === 'always') return false
  return true
}

export const isDirectPrefillEditMode = (element: any): boolean => {
  if (element?.prefillEditable === false) return false
  return true
}

export const usesPrefillEditFieldsMode = (element: any): boolean =>
  isConditionalPrefillEditMode(element)

export const ensurePrefillFieldSettings = (element: any): any => {
  const parentFieldId = Number(element.fieldId ?? element.id)
  const rawEditFields = Array.isArray(element.prefillEditFields) ? element.prefillEditFields : []
  const editFields = normalizePrefillEditFieldIds(
    rawEditFields,
    element.type === 'table' ? undefined : parentFieldId
  )
  // Respect explicit always mode; only infer editFields for legacy data with no mode set
  const inferredEditMode =
    element.prefillEditMode === 'always'
      ? 'always'
      : element.prefillEditMode === 'editFields' || element.prefillEditMode === 'conditional'
      ? element.prefillEditMode
      : editFields.length > 0
      ? 'editFields'
      : 'always'

  const base = {
    ...element,
    prefillEditable:
      element.prefillEditable !== undefined
        ? element.prefillEditable
        : defaultPrefillEditable(element.type),
    prefillEditMode: inferredEditMode,
    prefillEditableOnCondition:
      element.prefillEditableOnCondition || defaultPrefillEditableOnCondition(),
    prefillEditFields: editFields,
    useAsRecipientName: supportsRecipientNameToggle(element) && element.useAsRecipientName === true
  }

  if (element.type === 'table') {
    const cols = Number(element.tableColumnCount) || 3
    const rows = Number(element.tableRowCount) || 3
    const tableRowPrefillSettings = resizeTableRowPrefillSettings(
      element.tableRowPrefillSettings,
      rows
    ).map((rowConfig: any) => ({
      ...rowConfig,
      prefillEditFields: normalizePrefillEditFieldIds(rowConfig?.prefillEditFields)
    }))
    const tableColumnPrefillSettings = resizeTableColumnPrefillSettings(
      element.tableColumnPrefillSettings,
      cols
    ).map((columnConfig: any) => ({
      ...columnConfig,
      prefillEditFields: normalizePrefillEditFieldIds(columnConfig?.prefillEditFields)
    }))
    return {
      ...base,
      tableColumnPrefillSettings,
      tableRowPrefillSettings
    }
  }

  return base
}

export const supportsPrefillSettings = (type?: string): boolean =>
  !!type && PREFILL_SETTINGS_FIELD_TYPES.includes(type as any)

/** Keep at most one plain-input field flagged for email greeting names. */
export const normalizeSingleRecipientNameFlag = (formPages: any[][]): any[][] => {
  let enabledFieldKey: string | null = null

  return formPages.map((page, pageIndex) => {
    if (!Array.isArray(page)) return page
    return page.map((field, fieldIndex) => {
      if (!supportsRecipientNameToggle(field) || field.useAsRecipientName !== true) {
        if (field?.useAsRecipientName) {
          return { ...field, useAsRecipientName: false }
        }
        return field
      }

      const fieldKey = `${pageIndex}:${fieldIndex}`
      if (!enabledFieldKey) {
        enabledFieldKey = fieldKey
        return field
      }

      if (enabledFieldKey === fieldKey) {
        return field
      }

      return { ...field, useAsRecipientName: false }
    })
  })
}

export const createPrefillEditField = (
  parentFieldId: number,
  existingFields: PrefillEditFieldDefinition[],
  type = 'text',
  columnIndex?: number,
  rowIndex?: number
): PrefillEditFieldDefinition => {
  const isTableContext = rowIndex !== undefined || columnIndex !== undefined
  const usedIds = new Set(
    existingFields
      .map((field, index) =>
        resolvePrefillEditFieldId(field, index, isTableContext ? undefined : parentFieldId)
      )
      .filter((id) => Number.isFinite(id) && id > 0)
  )
  let nextIndex = existingFields.length + 1
  let fieldId = isTableContext
    ? nextIndex
    : Number(`${parentFieldId}${String(nextIndex).padStart(2, '0')}`)
  while (usedIds.has(fieldId)) {
    nextIndex += 1
    fieldId = isTableContext
      ? nextIndex
      : Number(`${parentFieldId}${String(nextIndex).padStart(2, '0')}`)
  }
  const typeLabel = PREFILL_EDIT_FIELD_TYPE_LABELS[type as PrefillEditFieldType] || type
  return {
    fieldId,
    type,
    label: `${typeLabel} ${nextIndex}`,
    placeholder: '',
    accept: type === 'file' ? '.pdf,.doc,.docx,.jpg,.jpeg,.png' : undefined,
    options: defaultOptionsForEditFieldType(type),
    isRequired: true,
    requiredErrorMessage: 'This field is required'
  }
}

/** Legacy edit fields without the flag stay required so existing forms keep working. */
export const isPrefillEditFieldRequired = (
  editField: { isRequired?: boolean } | null | undefined
): boolean => editField?.isRequired !== false

export const getPrefillEditFieldRequiredMessage = (
  editField: { label?: string; requiredErrorMessage?: string } | null | undefined
): string =>
  String(editField?.requiredErrorMessage || '').trim() ||
  (editField?.label ? `${editField.label} is required` : 'This field is required')
